/**
 * The account screen (front-door §6, front-door-impl §11.4 — brass-chromed):
 * email · passkeys · sessions · sign-out everywhere.
 *
 * The signed-in account registers + reviews its passkeys via
 * @simplewebauthn/browser, sees its active sessions, and can sign out
 * everywhere. House brass throughout (no agent accent). A passkey is the warm
 * one-tap upgrade over the OTP code; this is where it's added.
 */
import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { Button } from "@charlie/design/primitives";
import {
  whoami,
  fetchPasskeys,
  passkeyRegisterStart,
  passkeyRegisterFinish,
  fetchSessions,
  revokeAllSessions,
  type Account,
  type PasskeySummary,
  type SessionSummary,
} from "../lib/api";

export default function AccountPage() {
  const [account, setAccount] = useState<Account | null | "loading">("loading");
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const refresh = async () => {
    setPasskeys(await fetchPasskeys());
    setSessions(await fetchSessions());
  };

  useEffect(() => {
    void (async () => {
      const me = await whoami();
      setAccount(me.account);
      if (me.account) await refresh();
    })();
  }, []);

  // Sign out everywhere ELSE — spares this device, then refreshes the list so
  // only "this device" remains. (A full sign-out is just the per-device logout.)
  const signOutEverywhere = async () => {
    setSigningOut(true);
    const ok = await revokeAllSessions(true);
    if (ok) await refresh();
    setSigningOut(false);
  };

  const addPasskey = async () => {
    setBusy(true);
    setNote(null);
    try {
      const options = await passkeyRegisterStart();
      const response = await startRegistration({ optionsJSON: options as never });
      const ok = await passkeyRegisterFinish(response);
      setNote(ok ? "Passkey added." : "That didn't take — try again.");
      if (ok) await refresh();
    } catch {
      setNote("Passkey setup was cancelled or isn't available here.");
    } finally {
      setBusy(false);
    }
  };

  if (account === "loading") {
    return <div className="mx-auto max-w-md px-6 py-12 text-center text-ink-soft">…</div>;
  }
  if (account === null) {
    window.location.assign("/login");
    return null;
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col gap-6 px-6 py-12">
      <header className="flex flex-col gap-1">
        <p className="overline-label">Your account</p>
        <h1 className="font-display text-[24px] text-ink">{account.email}</h1>
      </header>

      <section className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-ink">Passkeys</h2>
            <p className="text-sm text-ink-soft">A faster, phishing-proof sign-in than a code.</p>
          </div>
          <Button variant="accent" disabled={busy} onClick={addPasskey}>
            {busy ? <span className="italic">waiting…</span> : "Add a passkey"}
          </Button>
        </div>
        {note && <p className="text-sm text-ink-soft">{note}</p>}
        {passkeys.length === 0 ? (
          <p className="text-sm text-ink-faint">No passkeys yet — you'll sign in with an emailed code until you add one.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {passkeys.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded-input border border-hairline bg-paper px-3 py-2">
                <span className="font-mono text-[12px] text-ink-soft">{p.id.slice(0, 18)}…</span>
                <span className="text-[12px] text-ink-faint">
                  added {new Date(p.createdAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-ink">Where you're signed in</h2>
            <p className="text-sm text-ink-soft">Each device that's signed in to the front door.</p>
          </div>
          <Button
            variant="quiet"
            disabled={signingOut || sessions.filter((s) => !s.current).length === 0}
            onClick={signOutEverywhere}
          >
            {signingOut ? <span className="italic">signing out…</span> : "Sign out everywhere"}
          </Button>
        </div>
        {sessions.length === 0 ? (
          <p className="text-sm text-ink-faint">No active sessions.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 rounded-input border border-hairline bg-paper px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm text-ink">
                    {s.src === "webauthn" ? "Passkey sign-in" : "Code sign-in"}
                    {s.current && <span className="ml-2 text-[12px] text-brass-deep">this device</span>}
                  </span>
                  <span className="text-[12px] text-ink-faint">
                    last active {new Date(s.lastSeen).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <a href="/" className="text-sm text-brass-deep underline-offset-2 hover:underline">
        ← Your Angels
      </a>
    </div>
  );
}
